import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Heart, Baby, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { PatientGeneralForm } from './PatientGeneralForm';
import { PatientCHDForm } from './PatientCHDForm';
import { PrematuritateDataTabs } from '@/components/prematuritate/PrematuritateDataTabs';
import { Patient } from '@/types/patient.types';
import { PatientFormData } from '@/utils/validation';
import { patientService } from '@/services/patientService';
import { diagnosisService } from '@/services/diagnosisService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';
import { DuplicatePatientDialog } from '@/components/patients/DuplicatePatientDialog';
import { ApiError, DuplicatePatientDetails } from '@/types/api.types';

type WizardStep = 'selection' | 'general' | 'specific' | 'completed';
type PatientType = 'chd' | 'prematurity' | null;

interface AddPatientWizardProps {
    onClose: () => void;
    onComplete: (patient: Patient) => void;
    onTakeoverRequest?: (cnp: string) => void;
}

export const AddPatientWizard: React.FC<AddPatientWizardProps> = ({
    onClose,
    onComplete,
    onTakeoverRequest,
}) => {
    const { utilizator } = useAuth();
    const { activeSection } = useSectionContext();
    const { toast } = useToast();
    const [currentStep, setCurrentStep] = useState<WizardStep>('selection');
    const [selectedType, setSelectedType] = useState<PatientType>(null);
    const [createdPatient, setCreatedPatient] = useState<Patient | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [duplicatePatient, setDuplicatePatient] = useState<DuplicatePatientDetails | null>(null);
    // Track if type was auto-determined from section (to hide back button)
    const [typeFromSection, setTypeFromSection] = useState(false);

    // Auto-determine patient type from active section
    useEffect(() => {
        if (activeSection) {
            const autoType: PatientType = activeSection === 'prematuri' ? 'prematurity' : 'chd';
            setSelectedType(autoType);
            setTypeFromSection(true);
            setCurrentStep('general');
        }
    }, [activeSection]);

    // Default values for the general form
    const defaultValues: PatientFormData = {
        name: '',
        birth_date: '',
        gender: 0,
        hospital_id: utilizator?.hospital_id || 1,
        cnp: '',
        county: '',
        locality: '',
        maternity: '',
        mother_name: '',
        father_name: '',
        notes: '',
        // Initialize other fields as undefined/empty
        icd10_code: '',
        syndrome: '',
        genetic_condition_icd10_code: null,
        data_quality_note: '',
        other_anomalies_icd10: '',
    };

    const handleTypeSelection = (type: PatientType) => {
        setSelectedType(type);
        setCurrentStep('general');
    };

    const handleGeneralSubmit = async (data: PatientFormData) => {
        try {
            setIsLoading(true);

            // Clean up data similar to PatientForm
            const cleanedData = {
                ...data,
                patient_code: data.patient_code || undefined,
                cnp: data.cnp || undefined,
                county: data.county || undefined,
                locality: data.locality || undefined,
                maternity: data.maternity || undefined,
                mother_name: data.mother_name || undefined,
                father_name: data.father_name || undefined,
                notes: data.notes || undefined,
                external_id: data.external_id || undefined,
                // Set is_premature flag if this is a premature patient
                is_premature: selectedType === 'prematurity' ? 1 : 0,
            };

            // Handle maternal data
            if (data.maternal_data) {
                // ... (Maternal data sanitization logic same as PatientForm)
                // For brevity, assuming the form passes valid data or backend handles it.
                // Ideally, we should reuse the sanitization logic.
                // Let's implement a basic check here.
                const hasMaternalValues = Object.values(data.maternal_data).some(v => v !== null && v !== false && v !== '');
                if (!hasMaternalValues) {
                    delete (cleanedData as any).maternal_data;
                }
            }

            const newPatient = await patientService.createPatient(cleanedData);
            setCreatedPatient(newPatient);

            toast({
                title: 'Pacient creat',
                description: 'Datele generale au fost salvate. Urmează datele specifice.',
            });

            setCurrentStep('specific');
        } catch (error: any) {
            const apiError = error as ApiError;
            if (apiError.code === 'PATIENT_CNP_EXISTS' && apiError.details) {
                setDuplicatePatient(apiError.details as DuplicatePatientDetails);
                return;
            }
            console.error('Error creating patient:', error);
            toast({
                title: 'Eroare',
                description: error.message || 'Nu s-a putut crea pacientul.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCHDSubmit = async (data: any) => {
        if (!createdPatient?.id) return;

        try {
            setIsLoading(true);

            // Update patient with MCC specific fields
            const chdData = {
                case_id: data.case_id,
                centre: data.centre,
                subgroup_chd: data.subgroup_chd,
                icd10_code: data.icd10_code,
                severe_chd: data.severe_chd,
                mca_flag: data.mca_flag,
                syndrome: data.syndrome,
                genetic_condition_icd10_code: data.genetic_condition_icd10_code,
                prenatal_dx: data.prenatal_dx,
                prenatal_method: data.prenatal_method,
                ga_prenatal_dx: data.ga_prenatal_dx,
                outcome_30d: data.outcome_30d,
                reintervention_1y: data.reintervention_1y,
                procedure_type: data.procedure_type,
                procedure_date: data.procedure_date,
                major_minor_flag: data.major_minor_flag,
                data_source: data.data_source,
                data_quality_note: data.data_quality_note,
                other_anomalies_icd10: data.other_anomalies_icd10,
            };

            // Merge with existing patient data as the backend requires core fields for updates
            const fullUpdateData = {
                ...chdData,
                name: createdPatient.name,
                birth_date: createdPatient.birth_date,
                gender: createdPatient.gender,
                hospital_id: createdPatient.hospital_id,
                cnp: createdPatient.cnp,
                county: createdPatient.county,
                locality: createdPatient.locality,
                maternity: createdPatient.maternity,
                mother_name: createdPatient.mother_name,
                father_name: createdPatient.father_name,
                notes: createdPatient.notes,
                patient_code: createdPatient.patient_code,
                external_id: createdPatient.external_id,
            };

            const updatedPatient = await patientService.updatePatient(createdPatient.id, fullUpdateData);

            // Note: Diagnostics are handled by DiagnosticSection internally via diagnosisService

            setCreatedPatient(updatedPatient);
            setCurrentStep('completed');

            toast({
                title: 'Date salvate',
                description: 'Datele MCC au fost actualizate cu succes.',
            });

            // Wait a bit then complete
            setTimeout(() => {
                onComplete(updatedPatient);
            }, 1500);

        } catch (error: any) {
            console.error('Error updating MCC data:', error);
            toast({
                title: 'Eroare',
                description: error.message || 'Nu s-au putut salva datele MCC.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrematurityFinish = () => {
        if (createdPatient) {
            setCurrentStep('completed');
            setTimeout(() => {
                onComplete(createdPatient);
            }, 1000);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 'selection':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
                        <Card
                            className="cursor-pointer hover:border-primary hover:bg-accent/5 transition-all"
                            onClick={() => handleTypeSelection('chd')}
                        >
                            <CardHeader className="text-center">
                                <Heart className="w-12 h-12 mx-auto text-red-500 mb-2" />
                                <CardTitle>Copil cu Malformație Cardiacă (MCC)</CardTitle>
                                <CardDescription>
                                    Adaugă un pacient cu date specifice pentru Registrul MCC și diagnostice cardiace.
                                </CardDescription>
                            </CardHeader>
                        </Card>

                        <Card
                            className="cursor-pointer hover:border-primary hover:bg-accent/5 transition-all"
                            onClick={() => handleTypeSelection('prematurity')}
                        >
                            <CardHeader className="text-center">
                                <Baby className="w-12 h-12 mx-auto text-blue-500 mb-2" />
                                <CardTitle>Copil prematur</CardTitle>
                                <CardDescription>
                                    Adaugă un pacient cu date specifice de prematuritate (formulare RONN).
                                </CardDescription>
                            </CardHeader>
                        </Card>
                    </div>
                );

            case 'general':
                return (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4">
                            {!typeFromSection && (
                                <Button variant="ghost" size="sm" onClick={() => setCurrentStep('selection')}>
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    Înapoi
                                </Button>
                            )}
                            <h2 className="text-lg font-semibold">
                                {typeFromSection ? 'Date generale' : 'Pasul 1: Date generale'}
                            </h2>
                            {typeFromSection && (
                                <span className="text-sm text-muted-foreground ml-2">
                                    ({selectedType === 'prematurity' ? 'Copil prematur' : 'MCC'})
                                </span>
                            )}
                        </div>
                        <PatientGeneralForm
                            defaultValues={defaultValues}
                            onSubmit={handleGeneralSubmit}
                            isLoading={isLoading}
                            submitLabel="Continuă spre date specifice"
                        />
                    </div>
                );

            case 'specific':
                return (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">
                                {typeFromSection ? 'Date specifice' : `Pasul 2: ${selectedType === 'chd' ? 'Date MCC' : 'Date prematuritate'}`}
                            </h2>
                            <div className="text-sm text-muted-foreground">
                                Pacient: <span className="font-medium text-foreground">{createdPatient?.name}</span>
                            </div>
                        </div>

                        {selectedType === 'chd' && createdPatient && (
                            <PatientCHDForm
                                patientId={createdPatient.id}
                                defaultValues={createdPatient as unknown as PatientFormData}
                                onSubmit={handleCHDSubmit}
                                isLoading={isLoading}
                                submitLabel="Finalizează"
                            />
                        )}

                        {selectedType === 'prematurity' && createdPatient && (
                            <div className="space-y-4">
                                <PrematuritateDataTabs
                                    patientId={createdPatient.id}
                                    patient={createdPatient}
                                />
                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={handlePrematurityFinish}>
                                        Finalizează
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 'completed':
                return (
                    <div className="flex flex-col items-center justify-center py-12 space-y-4">
                        <CheckCircle2 className="w-16 h-16 text-green-500" />
                        <h2 className="text-2xl font-bold">Pacient adăugat cu succes!</h2>
                        <p className="text-muted-foreground text-center max-w-md">
                            Pacientul {createdPatient?.name} a fost înregistrat în sistem împreună cu datele specifice.
                        </p>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="w-full max-w-5xl mx-auto">
            <DuplicatePatientDialog
                details={duplicatePatient}
                onClose={() => setDuplicatePatient(null)}
                onTakeover={onTakeoverRequest
                    ? (cnp) => {
                        setDuplicatePatient(null);
                        onClose();
                        onTakeoverRequest(cnp);
                    }
                    : undefined}
            />
            {renderStepContent()}
        </div>
    );
};
